import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { ClientBookingData } from "@/redux/apis/clientBooking/types";
import { departureLookupParams, toFlightDetails } from "@/redux/apis/clientFlight/clientFlightApi";
import { FlightDetails, GetFlightDetailsArgs } from "@/redux/apis/clientFlight/types";
import { baseQuery } from "@/redux/baseQuery";
import { GuestBookingTripLog } from "@/redux/slices/guest/types";
import { transformGuestBookingData, transformGuestBookingTripLog } from "./helpers";
import {
  CreateGuestBookingArgs,
  CreateGuestBookingResponse,
  GetGuestBookingTripLogParams,
  GuestEmailCheckResponse,
  GuestPaymentKeys,
  GuestQuote,
  GuestQuoteBody,
  GuestQuoteById,
  UploadGuestBookingAttachmentArgs,
} from "./types";

/**
 * Path for a guest attachment upload. The (session-less) guest is authorised by the (job id, secret)
 * pair carried in the URL — the same job+secret convention as the other guest job endpoints — so the
 * secret must land in the path, never a header/body. Exported so the contract is unit-testable without
 * driving a multipart request through the mock network (DEV-1252).
 */
export const guestDocumentUploadPath = (jobId: number, uploadKey: string): string => `/v3/guest/job/${jobId}/document/${uploadKey}`;

// Plain `baseQuery` (no path prefix) because the guest surface spans two roots: the TYPE_GUEST
// endpoints under /v3/guest/provider/{providerUuid}/... AND the flat public quote at /v2/guest-quote.
// Each endpoint spells its own full path. baseQuery still attaches a Kinde token IF present, but a
// guest has none, so these go out unauthenticated. The provider uuid is the resolved operator
// (operatorIdSelector), passed by the caller.
export const guestApi = createApi({
  reducerPath: "guestApi",
  baseQuery,
  endpoints: (builder) => ({
    getGuestBookingTripLog: builder.query<GuestBookingTripLog, GetGuestBookingTripLogParams>({
      query: ({ hash, jobId }) => `/v3/guest/job/${jobId}/trip-log/${hash}`,
      transformResponse: transformGuestBookingTripLog,
    }),
    // Guest booking reference data. Normalised to the shared ClientBookingData shape so
    // the wizard reads one contract regardless of scope — consumed via useBookingData.
    getGuestBookingData: builder.query<ClientBookingData, string>({
      query: (providerUuid) => `/v3/guest/provider/${providerUuid}/job/data`,
      transformResponse: transformGuestBookingData,
    }),
    // Guest Stripe publishable key. No SetupIntent — the guest tokenises client-side
    // via stripe.createPaymentMethod and the server attaches the pm_ to the minted account at submit.
    getGuestPaymentKeys: builder.query<GuestPaymentKeys, string>({
      query: (providerUuid) => `/v3/guest/provider/${providerUuid}/payment/keys/stripe`,
    }),
    // Guest live quote. A query (not mutation) so the summary re-prices automatically as the
    // draft changes; POST is the verb the v2 API exposes. The server re-prices — no client price is
    // ever trusted. The summary renders the returned quote (see GuestQuote) as the estimate.
    getGuestQuote: builder.query<GuestQuote, GuestQuoteBody>({
      query: (body) => ({ url: "/v2/guest-quote", method: "POST", body }),
    }),
    // Fetch a previously-computed guest quote by id. The external operator widget hands off to
    // the booking flow with only `?widget=v1&quote_uuid={id}`, so we refetch the quote and rehydrate the
    // draft from its `request` inputs (stops+coords, datetime, vehicle uuid, transfer id, extras). Public,
    // no auth — same flat /v2/guest-quote route as the POST.
    getGuestQuoteById: builder.query<GuestQuoteById, string>({
      query: (quoteId) => `/v2/guest-quote/${quoteId}`,
    }),
    // Email-existence check for the guest passenger step. Same public endpoint mobile uses:
    // 2xx { success: true, … } when the email already belongs to an account; 403 when it's free. Lets us
    // stop a guest who already has an account BEFORE they build the whole booking and prompt them to log in
    // (mirrors the create-time 409 ACCOUNT_EXISTS, but earlier for better UX).
    verifyGuestEmail: builder.mutation<GuestEmailCheckResponse, string>({
      query: (email) => ({ url: "/v3/auth/verify/email-address", method: "POST", body: { email_address: email } }),
    }),
    // Guest flight lookup. Provider-scoped, unauthenticated twin of the client flight lookup
    // (/v3/client/{id}/flight, DEV-474). Same Flight DTO → reuse toFlightDetails, so airport-pickup guests
    // resolve their flight without login. Consumed via the scope-aware useFlightDetails hook.
    getGuestFlightDetails: builder.query<
      FlightDetails,
      { providerUuid: string } & Pick<GetFlightDetailsArgs, "flightNumber" | "date" | "airport" | "direction" | "pickupLat" | "pickupLng">
    >({
      query: ({ providerUuid, flightNumber, date, airport, ...direction }) => ({
        url: `/v3/guest/provider/${providerUuid}/flight`,
        params: { flight_number: flightNumber, date, ...(airport ? { airport } : {}), ...departureLookupParams(direction) },
      }),
      transformResponse: toFlightDetails,
    }),
    // Guest booking-create. One request server-orchestrates: verify reCAPTCHA, mint the
    // account from the contact fields, attach the tokenised pm_, re-price, and place the booking. On
    // 409 { code: "ACCOUNT_EXISTS" } the email already belongs to a completed account — the caller
    // stashes the draft and routes to login. The server re-prices; no client price is ever trusted.
    createGuestBooking: builder.mutation<CreateGuestBookingResponse, CreateGuestBookingArgs>({
      query: ({ providerUuid, body }) => ({
        url: `/v3/guest/provider/${providerUuid}/booking`,
        method: "POST",
        body,
      }),
    }),
    // Guest attachment upload. Runs AFTER createGuestBooking returns a job_id + attachment_upload_key —
    // the booking must exist first (DEV-1252). Session-less, so authorisation is the (jobId, uploadKey)
    // pair carried in the URL path (the same job+secret convention as the other guest job endpoints);
    // the file rides a multipart FormData body (`file_upload`, `description`, `permission`).
    uploadGuestBookingAttachment: builder.mutation<void, UploadGuestBookingAttachmentArgs>({
      query: ({ jobId, uploadKey, body }) => ({
        url: guestDocumentUploadPath(jobId, uploadKey),
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetGuestBookingTripLogQuery,
  useLazyGetGuestBookingTripLogQuery,
  useGetGuestBookingDataQuery,
  useGetGuestPaymentKeysQuery,
  useGetGuestQuoteQuery,
  useGetGuestQuoteByIdQuery,
  useGetGuestFlightDetailsQuery,
  useVerifyGuestEmailMutation,
  useCreateGuestBookingMutation,
  useUploadGuestBookingAttachmentMutation,
} = guestApi;
